/**
 * A habit occurrence deep link (push). The habit page holds the day's editor
 * ("Each time" · today's Done today), so this route only finds the habit and
 * goes there — one shape for a habit (North Star rule 1, 2026-09-12). Before,
 * this was a second page with raw status words, an unzoned time and two
 * filled buttons.
 */
import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { habitExecutionService } from '../services/habitExecutionService'

export const HabitOccurrencePage: React.FC = () => {
  const { occurrenceId } = useParams<{ occurrenceId: string }>()
  const navigate = useNavigate()
  const [err, setErr] = useState<string | null>(null)
  const id = occurrenceId ? parseInt(occurrenceId, 10) : NaN

  useEffect(() => {
    if (!Number.isFinite(id)) { setErr('This link is not one we know.'); return }
    let alive = true
    habitExecutionService.getOccurrence(id)
      .then((o) => {
        if (!alive) return
        if (o?.habit_node_id) navigate(`/habits/${o.habit_node_id}`, { replace: true })
        else setErr('This one is not here any more.')
      })
      .catch((e: unknown) => { if (alive) setErr(e instanceof Error ? e.message : 'Could not open it') })
    return () => { alive = false }
  }, [id, navigate])

  if (!err) {
    return <div className="flex h-64 items-center justify-center"><RefreshCw className="h-6 w-6 animate-spin text-brand" /><span className="ml-3 text-secondary">Opening the habit…</span></div>
  }
  return (
    <div className="space-y-4">
      <Link to="/habits" className="inline-flex items-center gap-1 text-app-meta text-muted hover:text-brand"><ArrowLeft className="h-3.5 w-3.5" /> Habits</Link>
      <Card className="border-bad/30"><CardContent className="pt-6"><div className="py-8 text-center" role="alert">
        <AlertCircle className="mx-auto mb-4 h-12 w-12 text-bad" />
        <h2 className="mb-2 text-xl font-semibold text-primary">{err}</h2>
        <Button onClick={() => navigate('/habits')}>Go to habits</Button>
      </div></CardContent></Card>
    </div>
  )
}

export default HabitOccurrencePage
